import { lazy, Suspense, useEffect } from 'react';
import { createBrowserRouter, Navigate, RouterProvider, useRouteError } from 'react-router-dom';
import { ProtectedRoute } from './ProtectedRoute';
import { PageErrorBoundary } from '@/components/ui/ErrorBoundary';
import { Loader2 } from 'lucide-react';
import i18n from '@/lib/i18n';

// Loading fallback component
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="text-center">
        <Loader2 className="w-10 h-10 text-cyan-600 animate-spin mx-auto mb-4" />
        <p className="text-gray-500 dark:text-gray-400">{i18n.t('common.loading')}</p>
      </div>
    </div>
  );
}

// Global error element that handles chunk load errors
function GlobalErrorElement() {
  const error = useRouteError() as Error;

  useEffect(() => {
    // Check if this is a chunk loading error
    const isChunkError =
      error?.message?.includes('Failed to fetch dynamically imported module') ||
      error?.message?.includes('Loading chunk') ||
      error?.message?.includes('Loading CSS chunk') ||
      error?.name === 'ChunkLoadError';

    if (isChunkError) {
      // Check if we've already tried reloading recently (prevent infinite loops)
      const lastReload = sessionStorage.getItem('chunk-error-reload');
      const now = Date.now();

      if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
        sessionStorage.setItem('chunk-error-reload', now.toString());
        window.location.reload();
      }
    }
  }, [error]);

  // Show loading spinner while reloading
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="text-center p-8">
        <div className="animate-spin w-8 h-8 border-4 border-cyan-600 border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-gray-600 dark:text-gray-400">
          Az alkalmazás frissül...
        </p>
      </div>
    </div>
  );
}

// Auth pages - lazy loaded
const LoginPage = lazy(() => import('@/pages/auth/LoginPage').then(m => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage').then(m => ({ default: m.RegisterPage })));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const VerifyEmailPage = lazy(() => import('@/pages/auth/VerifyEmailPage').then(m => ({ default: m.VerifyEmailPage })));
const AuthCallbackPage = lazy(() => import('@/pages/auth/AuthCallbackPage').then(m => ({ default: m.AuthCallbackPage })));

// Dashboard - lazy loaded
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage').then(m => ({ default: m.DashboardPage })));

// Jewelry pages - lazy loaded
const JewelryListPage = lazy(() => import('@/pages/jewelry/JewelryListPage').then(m => ({ default: m.JewelryListPage })));
const JewelryDetailPage = lazy(() => import('@/pages/jewelry/JewelryDetailPage').then(m => ({ default: m.JewelryDetailPage })));
const JewelryActivationPage = lazy(() => import('@/pages/activate/JewelryActivationPage').then(m => ({ default: m.JewelryActivationPage })));
const JewelryPublicPage = lazy(() => import('@/pages/public/JewelryPublicPage').then(m => ({ default: m.JewelryPublicPage })));
const GuestLinkPublicPage = lazy(() => import('@/pages/public/GuestLinkPublicPage').then(m => ({ default: m.GuestLinkPublicPage })));

// Warranty pages - lazy loaded
const WarrantyClaimsPage = lazy(() => import('@/pages/warranty/WarrantyClaimsPage').then(m => ({ default: m.WarrantyClaimsPage })));
const WarrantyNewClaimPage = lazy(() => import('@/pages/warranty/WarrantyNewClaimPage').then(m => ({ default: m.WarrantyNewClaimPage })));
const WarrantyClaimDetailPage = lazy(() => import('@/pages/warranty/WarrantyClaimDetailPage').then(m => ({ default: m.WarrantyClaimDetailPage })));

// Settings pages - lazy loaded
const AccountSettingsPage = lazy(() => import('@/pages/settings/AccountSettingsPage').then(m => ({ default: m.AccountSettingsPage })));
const ProfileSettingsPage = lazy(() => import('@/pages/settings/ProfileSettingsPage').then(m => ({ default: m.ProfileSettingsPage })));
const SecuritySettingsPage = lazy(() => import('@/pages/settings/SecuritySettingsPage').then(m => ({ default: m.SecuritySettingsPage })));
const BillingSettingsPage = lazy(() => import('@/pages/settings/BillingSettingsPage').then(m => ({ default: m.BillingSettingsPage })));
const NotificationSettingsPage = lazy(() => import('@/pages/settings/NotificationSettingsPage').then(m => ({ default: m.NotificationSettingsPage })));
const ActivityLogPage = lazy(() => import('@/pages/settings/ActivityLogPage').then(m => ({ default: m.ActivityLogPage })));
const LocaleSettingsPage = lazy(() => import('@/pages/settings/LocaleSettingsPage').then(m => ({ default: m.LocaleSettingsPage })));
const GiftsPage = lazy(() => import('@/pages/settings/GiftsPage').then(m => ({ default: m.GiftsPage })));
const PrivacySettingsPage = lazy(() => import('@/pages/settings/PrivacySettingsPage').then(m => ({ default: m.PrivacySettingsPage })));

// Admin pages - lazy loaded
const AdminDashboardPage = lazy(() => import('@/pages/admin/AdminDashboardPage').then(m => ({ default: m.AdminDashboardPage })));
const AdminPlansPage = lazy(() => import('@/pages/admin/AdminPlansPage').then(m => ({ default: m.AdminPlansPage })));
const AdminTicketsPage = lazy(() => import('@/pages/admin/AdminTicketsPage').then(m => ({ default: m.AdminTicketsPage })));
const AdminUsersPage = lazy(() => import('@/pages/admin/AdminUsersPage').then(m => ({ default: m.AdminUsersPage })));
const AdminAnalyticsPage = lazy(() => import('@/pages/admin/AdminAnalyticsPage').then(m => ({ default: m.AdminAnalyticsPage })));
const AdminAuditPage = lazy(() => import('@/pages/admin/AdminAuditPage').then(m => ({ default: m.AdminAuditPage })));
const AdminDevicesPage = lazy(() => import('@/pages/admin/AdminDevicesPage').then(m => ({ default: m.AdminDevicesPage })));
const AdminPoliciesPage = lazy(() => import('@/pages/admin/AdminPoliciesPage').then(m => ({ default: m.AdminPoliciesPage })));
const AdminMusicPage = lazy(() => import('@/pages/admin/AdminMusicPage').then(m => ({ default: m.AdminMusicPage })));
const AdminPromosPage = lazy(() => import('@/pages/admin/AdminPromosPage').then(m => ({ default: m.AdminPromosPage })));
const AdminApprovalsPage = lazy(() => import('@/pages/admin/AdminApprovalsPage').then(m => ({ default: m.AdminApprovalsPage })));
const AdminDisputesPage = lazy(() => import('@/pages/admin/AdminDisputesPage').then(m => ({ default: m.AdminDisputesPage })));
const AdminHelpPage = lazy(() => import('@/pages/admin/AdminHelpPage').then(m => ({ default: m.AdminHelpPage })));
const AdminModerationPage = lazy(() => import('@/pages/admin/AdminModerationPage').then(m => ({ default: m.AdminModerationPage })));
const AdminAiConfigPage = lazy(() => import('@/pages/admin/AdminAiConfigPage').then(m => ({ default: m.AdminAiConfigPage })));
const AdminBillingPage = lazy(() => import('@/pages/admin/AdminBillingPage').then(m => ({ default: m.AdminBillingPage })));
const AdminAbusePage = lazy(() => import('@/pages/admin/AdminAbusePage').then(m => ({ default: m.AdminAbusePage })));
const AdminFreezePage = lazy(() => import('@/pages/admin/AdminFreezePage').then(m => ({ default: m.AdminFreezePage })));
const AdminMarketingPage = lazy(() => import('@/pages/admin/AdminMarketingPage').then(m => ({ default: m.AdminMarketingPage })));
const AdminDigitalLegacyPage = lazy(() => import('@/pages/admin/AdminDigitalLegacyPage').then(m => ({ default: m.AdminDigitalLegacyPage })));
const AdminSecurityPage = lazy(() => import('@/pages/admin/AdminSecurityPage').then(m => ({ default: m.AdminSecurityPage })));
const ExecutiveDashboardPage = lazy(() => import('@/pages/admin/ExecutiveDashboardPage'));

// Webshop pages - lazy loaded
const WebshopPage = lazy(() => import('@/pages/webshop/WebshopPage').then(m => ({ default: m.WebshopPage })));

// Help pages - lazy loaded
const HelpSupportPage = lazy(() => import('@/pages/help/HelpSupportPage').then(m => ({ default: m.HelpSupportPage })));

// Rewards pages - lazy loaded
const RewardsPage = lazy(() => import('@/pages/rewards/RewardsPage').then(m => ({ default: m.RewardsPage })));

// Other pages - lazy loaded
const PrivacyPage = lazy(() => import('@/pages/privacy/PrivacyPage').then(m => ({ default: m.PrivacyPage })));
const TransferAcceptPage = lazy(() => import('@/pages/transfer/TransferAcceptPage').then(m => ({ default: m.TransferAcceptPage })));
const RedeemGiftPage = lazy(() => import('@/pages/gift/RedeemGiftPage').then(m => ({ default: m.RedeemGiftPage })));
const DeviceActivationPage = lazy(() => import('@/pages/device/DeviceActivationPage').then(m => ({ default: m.DeviceActivationPage })));
const DeviceDetailPage = lazy(() => import('@/pages/device/DeviceDetailPage').then(m => ({ default: m.DeviceDetailPage })));
const TrashPage = lazy(() => import('@/pages/trash/TrashPage').then(m => ({ default: m.TrashPage })));
const DigitalLegacyPage = lazy(() => import('@/pages/legacy/DigitalLegacyPage').then(m => ({ default: m.DigitalLegacyPage })));
const CollaboratorHubPage = lazy(() => import('@/pages/collaborators/CollaboratorHubPage').then(m => ({ default: m.CollaboratorHubPage })));

// Legal pages - lazy loaded
const LegalPolicyPage = lazy(() => import('@/pages/legal/LegalPolicyPage').then(m => ({ default: m.LegalPolicyPage })));

// Digital Legacy Claim page - lazy loaded
const ClaimLegacyPage = lazy(() => import('@/pages/claim/ClaimLegacyPage').then(m => ({ default: m.ClaimLegacyPage })));

const router = createBrowserRouter([
  {
    // Root route with global error handler for chunk load errors
    errorElement: <GlobalErrorElement />,
    children: [
  // Public routes
  {
    path: '/login',
    element: (
      <Suspense fallback={<PageLoader />}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    path: '/register',
    element: (
      <Suspense fallback={<PageLoader />}>
        <RegisterPage />
      </Suspense>
    ),
  },
  {
    path: '/forgot-password',
    element: (
      <Suspense fallback={<PageLoader />}>
        <ForgotPasswordPage />
      </Suspense>
    ),
  },
  {
    path: '/reset-password/:token',
    element: (
      <Suspense fallback={<PageLoader />}>
        <ResetPasswordPage />
      </Suspense>
    ),
  },
  {
    path: '/verify-email/:token',
    element: (
      <Suspense fallback={<PageLoader />}>
        <VerifyEmailPage />
      </Suspense>
    ),
  },
  {
    path: '/auth/callback',
    element: (
      <Suspense fallback={<PageLoader />}>
        <AuthCallbackPage />
      </Suspense>
    ),
  },

  // NFC Public View (public - /j/:code)
  {
    path: '/j/:code',
    element: (
      <Suspense fallback={<PageLoader />}>
        <JewelryPublicPage />
      </Suspense>
    ),
  },

  // Guest Link Public View (public - /g/:token)
  {
    path: '/g/:token',
    element: (
      <Suspense fallback={<PageLoader />}>
        <GuestLinkPublicPage />
      </Suspense>
    ),
  },

  // Legal/Policy pages (public)
  {
    path: '/legal/:type',
    element: (
      <Suspense fallback={<PageLoader />}>
        <LegalPolicyPage />
      </Suspense>
    ),
  },

  // Webshop (public preview - links to external shop later)
  {
    path: '/shop',
    element: (
      <Suspense fallback={<PageLoader />}>
        <WebshopPage />
      </Suspense>
    ),
  },

  // Device activation (legacy - /d/:code)
  {
    path: '/d/:code',
    element: (
      <Suspense fallback={<PageLoader />}>
        <DeviceActivationPage />
      </Suspense>
    ),
  },

  // Transfer accept (semi-protected - shows login prompt if not authenticated)
  {
    path: '/transfer/accept/:token',
    element: (
      <Suspense fallback={<PageLoader />}>
        <TransferAcceptPage />
      </Suspense>
    ),
  },

  // Gift redemption (semi-protected - shows login prompt if not authenticated)
  {
    path: '/gift/:token',
    element: (
      <Suspense fallback={<PageLoader />}>
        <RedeemGiftPage />
      </Suspense>
    ),
  },

  // Digital Legacy Claim (public - allows creating account or using existing)
  {
    path: '/claim/:token',
    element: (
      <Suspense fallback={<PageLoader />}>
        <ClaimLegacyPage />
      </Suspense>
    ),
  },

  // Protected routes
  {
    path: '/dashboard',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <DashboardPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },

  // Jewelry routes
  {
    path: '/jewelry',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <JewelryListPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/jewelry/:jewelryId',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <JewelryDetailPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },

  // Warranty routes
  {
    path: '/jewelry/:jewelryId/warranty',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <WarrantyClaimsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/jewelry/:jewelryId/warranty/new',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <WarrantyNewClaimPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/warranty/claims/:claimId',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <WarrantyClaimDetailPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },

  // Jewelry activation
  {
    path: '/activate',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <JewelryActivationPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },

  // Settings routes
  {
    path: '/settings',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <AccountSettingsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings/profile',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <ProfileSettingsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings/security',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <SecuritySettingsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings/billing',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <BillingSettingsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings/notifications',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <NotificationSettingsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings/activity',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <ActivityLogPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings/locale',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <LocaleSettingsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings/gifts',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <GiftsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings/privacy',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <PrivacySettingsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },

  // Rewards & Referral
  {
    path: '/rewards',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <RewardsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },

  // Help
  {
    path: '/help',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <HelpSupportPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },

  // Privacy / GDPR
  {
    path: '/privacy',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <PrivacyPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },

  // Trash / Kuka
  {
    path: '/trash',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <TrashPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },

  // Digital Legacy Hub
  {
    path: '/digital-legacy',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <DigitalLegacyPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },

  // Collaborator Hub
  {
    path: '/collaborators',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <CollaboratorHubPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },

  // Admin routes (SUPERADMIN and ADMIN only)
  {
    path: '/admin',
    element: (
      <ProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']}>
        <Suspense fallback={<PageLoader />}>
          <AdminDashboardPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/plans',
    element: (
      <ProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']}>
        <Suspense fallback={<PageLoader />}>
          <AdminPlansPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/tickets',
    element: (
      <ProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']}>
        <Suspense fallback={<PageLoader />}>
          <AdminTicketsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/users',
    element: (
      <ProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']}>
        <Suspense fallback={<PageLoader />}>
          <AdminUsersPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/analytics',
    element: (
      <ProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']}>
        <Suspense fallback={<PageLoader />}>
          <AdminAnalyticsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/analytics/executive',
    element: (
      <ProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']}>
        <Suspense fallback={<PageLoader />}>
          <ExecutiveDashboardPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/audit',
    element: (
      <ProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']}>
        <Suspense fallback={<PageLoader />}>
          <AdminAuditPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/devices',
    element: (
      <ProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']}>
        <Suspense fallback={<PageLoader />}>
          <AdminDevicesPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/policies',
    element: (
      <ProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']}>
        <Suspense fallback={<PageLoader />}>
          <AdminPoliciesPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/music',
    element: (
      <ProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']}>
        <Suspense fallback={<PageLoader />}>
          <AdminMusicPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/promos',
    element: (
      <ProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']}>
        <Suspense fallback={<PageLoader />}>
          <AdminPromosPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/approvals',
    element: (
      <ProtectedRoute allowedRoles={['SUPERADMIN']}>
        <Suspense fallback={<PageLoader />}>
          <AdminApprovalsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/disputes',
    element: (
      <ProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']}>
        <Suspense fallback={<PageLoader />}>
          <AdminDisputesPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/help',
    element: (
      <ProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']}>
        <Suspense fallback={<PageLoader />}>
          <AdminHelpPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/moderation',
    element: (
      <ProtectedRoute allowedRoles={['SUPERADMIN']}>
        <Suspense fallback={<PageLoader />}>
          <AdminModerationPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/moderation/ai-config',
    element: (
      <ProtectedRoute allowedRoles={['SUPERADMIN']}>
        <Suspense fallback={<PageLoader />}>
          <AdminAiConfigPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/billing',
    element: (
      <ProtectedRoute allowedRoles={['ADMIN', 'SUPERADMIN']}>
        <Suspense fallback={<PageLoader />}>
          <AdminBillingPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/abuse',
    element: (
      <ProtectedRoute allowedRoles={['ADMIN', 'SUPERADMIN']}>
        <Suspense fallback={<PageLoader />}>
          <AdminAbusePage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/freeze',
    element: (
      <ProtectedRoute allowedRoles={['ADMIN', 'SUPERADMIN']}>
        <Suspense fallback={<PageLoader />}>
          <AdminFreezePage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/marketing',
    element: (
      <ProtectedRoute allowedRoles={['ADMIN', 'SUPERADMIN']}>
        <Suspense fallback={<PageLoader />}>
          <AdminMarketingPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/digital-legacy',
    element: (
      <ProtectedRoute allowedRoles={['ADMIN', 'SUPERADMIN']}>
        <Suspense fallback={<PageLoader />}>
          <AdminDigitalLegacyPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/security',
    element: (
      <ProtectedRoute allowedRoles={['SUPERADMIN']}>
        <Suspense fallback={<PageLoader />}>
          <AdminSecurityPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },

  // Legacy device routes (redirect to jewelry)
  {
    path: '/devices',
    element: <Navigate to="/jewelry" replace />,
  },
  {
    path: '/devices/:deviceId',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <DeviceDetailPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },

  // Redirects
  {
    path: '/',
    element: <Navigate to="/dashboard" replace />,
  },
  {
    path: '*',
    element: <Navigate to="/dashboard" replace />,
  },
    ],
  },
]);

export function AppRouter() {
  return (
    <PageErrorBoundary>
      <RouterProvider router={router} />
    </PageErrorBoundary>
  );
}
